import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { websocketService, ConnectionState } from '../services/websocketService';

export interface WebSocketStatusProps {
    showDetails?: boolean;
    className?: string;
}

/**
 * WebSocket 连接状态指示器（纯 CSS，不依赖 Tailwind）。
 * showDetails=false：侧栏底部的迷你徽标；showDetails=true：设置面板中的详细状态。
 */
const WebSocketStatus: React.FC<WebSocketStatusProps> = ({
    showDetails = false,
    className = ''
}) => {
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        isConnected: false,
        isConnecting: false,
        reconnectAttempts: 0
    });

    useEffect(() => {
        const unsubscribe = websocketService.onStateChange((state) => {
            setConnectionState(state);
        });
        return () => {
            unsubscribe();
        };
    }, []);

    const handleReconnect = () => {
        websocketService.forceReconnect();
    };

    const { isConnected, isConnecting, reconnectAttempts, lastError } = connectionState;

    const palette = isConnecting
        ? { color: '#d48806', bg: '#fffbe6', border: '#ffe58f' }
        : isConnected
            ? { color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f' }
            : { color: '#cf1322', bg: '#fff1f0', border: '#ffa39e' };

    const statusText = isConnecting ? '连接中...' : isConnected ? '已连接' : '连接断开';

    const icon = isConnecting
        ? <RefreshCw size={12} className="ws-status-spin" />
        : isConnected
            ? <Wifi size={12} />
            : <WifiOff size={12} />;

    return (
        <div className={`websocket-status ${className}`}>
            <div
                title={isConnected ? 'WebSocket 已连接' : lastError || 'WebSocket 未连接'}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: showDetails ? '4px 10px' : '2px 8px',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                    color: palette.color, backgroundColor: palette.bg,
                    border: `1px solid ${palette.border}`,
                }}
            >
                {icon}
                <span>{statusText}</span>

                {showDetails && reconnectAttempts > 0 && (
                    <span style={{ fontSize: '11px', opacity: 0.75 }}>
                        (重连 {reconnectAttempts}/10)
                    </span>
                )}

                {showDetails && !isConnected && !isConnecting && (
                    <button
                        onClick={handleReconnect}
                        style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            color: 'inherit', fontSize: '11px', textDecoration: 'underline',
                            padding: 0,
                        }}
                    >
                        重连
                    </button>
                )}
            </div>

            {showDetails && lastError && (
                <div style={{
                    marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '4px',
                    fontSize: '11px', color: '#cf1322',
                }}>
                    <AlertCircle size={12} style={{ marginTop: '1px', flexShrink: 0 }} />
                    <span>{lastError}</span>
                </div>
            )}

            {/* 连接中的旋转动画 */}
            <style>{`
                .ws-status-spin { animation: wsStatusSpin 1s linear infinite; }
                @keyframes wsStatusSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default WebSocketStatus;
